import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { MissedCallVisual } from '../components/visuals/MissedCallVisual';
import { CalendarVisual } from '../components/visuals/CalendarVisual';
import { SplitSection, ChecklistSection, CompareTable, ProofStrip, FaqSection, PriceSection, RelatedLinks, serviceJsonLd } from '../components/LocalPage';

/* 2026-09-17: money page 1 of 5 from the 2026-08-13 keyword realignment.
   Target query: "answering service for contractors" (exact match in title,
   H1 and first paragraph). Written for the owner on a ladder, not for an
   office manager comparing vendors. No reviews, no reputation, no $1,197. */

const faqs = [{
  question: 'What does an answering service for contractors actually do?',
  answer: 'It picks up when you cannot. The difference between services is what happens next. A basic one takes a message. Ours asks the questions you would ask (what is the problem, where is the property, when works), books the estimate on your calendar, and texts you the details. The caller hangs up with an appointment instead of a promise that someone will call back.'
}, {
  question: 'Is this a real person or an AI?',
  answer: 'An AI receptionist, trained on your business. It knows your trade, your services, your service area and your hours, and it is built and tuned with you during onboarding. Callers get a natural conversation, not a phone tree. Anything it cannot handle gets routed to you or logged as a message with the full transcript.'
}, {
  question: 'Can it book jobs directly on my calendar?',
  answer: 'Yes. It reads your availability and books into the CRM calendar that comes with The Viridia System. You get a text and a calendar entry. If you would rather approve every booking yourself, it can hold the slot as a request instead.'
}, {
  question: 'What happens with emergency calls?',
  answer: 'You decide the rules. A burst pipe at 2 AM can ring straight through to your cell, get booked for the first slot in the morning, or both. Most plumbers and HVAC owners set a short list of words that trigger an immediate transfer and let everything else book normally.'
}, {
  question: 'How much does an answering service for contractors cost?',
  answer: 'Traditional services bill per minute or per call, so a busy month costs more than a slow one. The AI receptionist is part of The Viridia System at $897 a month, month to month, and that price includes the CRM, missed-call text-back, your business number and the follow-up automations. It does not go up when the phone rings more.'
}, {
  question: 'How long does setup take?',
  answer: 'Most contractors are live within a week. We build the receptionist with you on a setup call, register your business number for texting, forward or port your existing line, and test it with real calls before it goes live.'
}, {
  question: 'Do you only work with Phoenix contractors?',
  answer: 'Phoenix is home and most of our clients are here, from Buckeye to Queen Creek. The system itself works anywhere in the US. If you are outside Arizona, book the call and we will tell you honestly whether it fits.'
}];
const jsonLd = serviceJsonLd({
  name: 'Answering Service for Contractors',
  serviceType: 'AI answering service for contractors',
  description: 'AI receptionist for contractors that answers every call, texts back missed calls, and books estimates on your calendar. Part of The Viridia System, $897 a month.',
  path: '/answering-service-for-contractors'
});
export function AnsweringServiceForContractors() {
  return <>
      <SEOHead title="Answering Service for Contractors | Books the Job, Not a Message | Viridia" description="An answering service for contractors that picks up every call, texts back missed ones, and books the estimate while you are on the job. AI receptionist built in Phoenix. $897 a month, month to month." canonical="/answering-service-for-contractors" jsonLd={jsonLd} faq={faqs} data-id="element-1107" data-mp="3rcml3oc5gbvt" />

      <PageHeader label="Reception" title="Answering service for contractors." intro="An answering service for contractors should do more than take a message. Ours picks up every call, texts back every missed one, and books the estimate while you are still on the job." data-id="element-1108" data-mp="3srp9nq3jztfk">
        <div className="flex flex-wrap items-center gap-4" data-id="element-1109" data-mp="kg35dtc55wsb">
          <CTAButton to="/contact" data-id="element-1110" data-mp="fdvqovzxg2ma">Book a 15-min call</CTAButton>
          <CTAButton to="/pricing#viridia-system" variant="ghost" data-id="element-1111" data-mp="353zcsouequby">
            See pricing
          </CTAButton>
        </div>
      </PageHeader>

      <SplitSection label="The problem" title="You are on a roof. The phone rings four times and goes to voicemail." body={<>
            <p data-id="element-1113" data-mp="1klngx1nwk4ga">
              Contractors miss calls for a good reason: you are working. The caller does not know
              that. They hear voicemail, hang up, and tap the next name in the search results.
            </p>
            <p data-id="element-1114" data-mp="1ucrupy1udj9j">
              Most of those callers never leave a message and never call back. That is the job you
              did not know you lost.
            </p>
          </>} visual={<MissedCallVisual data-id="element-1115" data-mp="23bmxf05ogb83" />} note="The leak" data-id="element-1112" data-mp="304cvo4bgktxv" />

      <SplitSection alt flip label="What answers" title="Somebody picks up, asks the right questions, and books it." body={<>
            <p data-id="element-1117" data-mp="2d8hz1rx8yrql">
              The AI receptionist answers on the first ring, day or night. It knows your trade, your
              services and your service area, because you built it with us.
            </p>
            <p data-id="element-1118" data-mp="aw5b36c26p26">
              It asks what is wrong, where the property is, and when works. Then it books the
              estimate on your calendar and texts you the details before the caller has put the
              phone down.
            </p>
          </>} visual={<DeviceMockup mode="call" heading="Incoming call, 11:42 AM" lines={[{
      from: 'caller',
      text: 'Hi, my AC is blowing warm air and it is 108 out. Can someone come today?'
    }, {
      from: 'ai',
      text: 'Yes. What is the address, and is 3:30 this afternoon okay for the tech?'
    }, {
      from: 'caller',
      text: '3:30 works. 4412 West Cactus Road.'
    }]} resultChip="Booked for 3:30 PM, owner texted" data-id="element-1119" data-mp="kryk4fqkk0f7" />} note="AI receptionist" data-id="element-1116" data-mp="28c0y1x4y8iw3" />

      <SplitSection label="The backstop" title="If a call slips through, a text goes out in seconds." body={<>
            <p data-id="element-1121" data-mp="18i2nqn3rc067">
              Missed-call text-back sends a message while the caller is still holding the phone.
              Most people reply to a text faster than they call a second number.
            </p>
            <p data-id="element-1122" data-mp="1i7hylbig25pt">
              The conversation lands in one inbox with your calls, Yelp messages and web forms, so
              you can pick it up between jobs.
            </p>
          </>} visual={<DeviceMockup mode="chat" heading="Missed call, 40 seconds later" lines={[{
      from: 'system',
      text: 'Sorry we missed you. This is Sonoran Roofing. What can we help with?'
    }, {
      from: 'customer',
      text: 'Have a leak near the chimney, need someone to look at it.'
    }, {
      from: 'system',
      text: 'We can be out Thursday morning. What is the address?'
    }]} resultChip="Lead saved" data-id="element-1123" data-mp="18h6hen9r200z" />} note="Missed-call text-back" data-id="element-1120" data-mp="1dk1qburoat23" />

      <ChecklistSection alt label="What a contractor needs" title="Six things an answering service for contractors has to get right." body="Most services do the first one. We built the system around all six." columns={3} items={[{
      title: 'Answers 24 hours a day',
      body: 'Nights, weekends, monsoon season. The call after 6 PM is the one that becomes a job for whoever answers it.'
    }, {
      title: 'Knows your trade',
      body: 'A roofer gets asked about the leak. A painter gets asked interior or exterior. The receptionist is built for your services, not a generic script.'
    }, {
      title: 'Knows your area',
      body: 'It knows you go to Peoria and Surprise but not Tucson, so you are not driving an hour for an estimate you would have turned down.'
    }, {
      title: 'Books the estimate',
      body: 'Straight onto your calendar with the address and the problem. Not a sticky note that says call back.'
    }, {
      title: 'Texts back missed calls',
      body: 'The backstop for the call that got through anyway. A text in seconds, in your name.'
    }, {
      title: 'Keeps following up',
      body: 'The lead that said maybe gets a reminder tomorrow and next week, on its own, until they book or say no.'
    }]} data-id="element-1124" data-mp="uh5gj7thisli" />

      <section className="border-b border-[#1a1a1a]" data-id="element-1125" data-mp="36oaptghhgj8t">
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-1126" data-mp="3bl10tpefkond">
          <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-1127" data-mp="3gknhk8tgwn0x">
            Compared to the other ways contractors handle the phone.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-1128" data-mp="3ld6vx7zzbm6s">
            Honest comparison. A live answering service is better than voicemail. It is just not
            built to book your jobs.
          </p>
          <div className="mt-12" data-id="element-1129" data-mp="1k9lwaw5umlqt">
            <CompareTable columns={['Voicemail', 'Live answering service', 'Viridia AI receptionist']} highlight={2} rows={[{
            criterion: 'Picks up every call',
            cells: [false, 'Business hours or extra fee', true]
          }, {
            criterion: 'Knows your services and area',
            cells: [false, 'Reads a script', true]
          }, {
            criterion: 'Books the estimate on your calendar',
            cells: [false, 'Takes a message', true]
          }, {
            criterion: 'Texts back a missed call',
            cells: [false, false, true]
          }, {
            criterion: 'Follows up on its own',
            cells: [false, false, true]
          }, {
            criterion: 'Pricing',
            cells: ['Free, and it costs you jobs', 'Per minute or per call', 'Flat, $897 a month, all in']
          }]} data-id="element-1130" data-mp="1k8kiwl1d1n5x" />
          </div>
        </div>
      </section>

      <ProofStrip items={[{
      value: '28',
      label: 'estimates booked by the AI receptionist for a Phoenix landscaper',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '29',
      label: 'estimates booked by the AI receptionist for a Valley painting company',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }, {
      value: '$15k',
      label: 'in closed-won work traced to those painting estimates',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }]} data-id="element-1131" data-mp="mswuv3kt9lhy" />

      <SplitSection label="Trades" title="Built for the trades that live on the phone." body={<>
            <p data-id="element-1133" data-mp="3irxo4qduonij">
              Roofing, painting, landscaping, plumbing, HVAC, electrical, countertops, garage doors,
              windows, remodeling. If the job starts with a call and an estimate, this fits.
            </p>
            <p data-id="element-1134" data-mp="39pg2oe60rywl">
              Every receptionist is set up for one business. Yours will not sound like the one we
              built for the plumber down the road.
            </p>
          </>} visual={<CalendarVisual mode="selfBook" heading="Your calendar, filling itself" data-id="element-1135" data-mp="2v5lpk9ecdgm9" />} note="One system" data-id="element-1132" data-mp="3npz8rfqxcuw4" />

      <PriceSection alt title="The receptionist comes with the whole system." body={<p data-id="element-1137" data-mp="2q462kbslt018">
            The AI receptionist, the CRM, missed-call text-back, a business phone number, the
            follow-up automations and a setup call with us. One price, no per-minute billing, no
            contract.
          </p>} data-id="element-1136" data-mp="30097fpeubcrv" />

      <FaqSection title="Questions contractors ask before they switch." faqs={faqs} data-id="element-1138" data-mp="3isbov2zb2x36" />

      <RelatedLinks title="Related pages" links={[{
      to: '/answering-service-phoenix',
      label: 'Answering service in Phoenix',
      body: 'The local page: service area, hours, and who we already answer for in the Valley.'
    }, {
      to: '/missed-call-text-back',
      label: 'Missed-call text-back',
      body: 'How the text goes out, what it says, and why callers reply to it.'
    }, {
      to: '/crm-for-contractors',
      label: 'CRM for contractors',
      body: 'Where every call, text and lead lands, and how the follow-up runs itself.'
    }]} data-id="element-1139" data-mp="343fgf4tc7i21" />

      <ClosingCTA data-id="element-1140" data-mp="1zuov4ev9912u" />
    </>;
}